import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { adminAPI } from '../../lib/api';
import { toast } from 'sonner';
import { 
  Search, Eye, ShoppingCart, Package, CreditCard, User, Calendar, 
  RefreshCw, Trash2, Archive, XCircle, CheckCircle2, AlertTriangle, X
} from 'lucide-react';
import { formatPrice, formatDate } from '../../lib/utils';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/utils';

const STATUS_CONFIG = {
  pending: { bg: 'bg-[#FEF3C7]', text: 'text-[#D97706]', label: 'Pending' },
  completed: { bg: 'bg-[#DCFCE7]', text: 'text-[#16A34A]', label: 'Completed' },
  failed: { bg: 'bg-[#FEE2E2]', text: 'text-[#DC2626]', label: 'Failed' },
  refunded: { bg: 'bg-[#F5F5F5]', text: 'text-[#525252]', label: 'Refunded' },
  cancelled: { bg: 'bg-[#FEE2E2]', text: 'text-[#DC2626]', label: 'Cancelled' },
  archived: { bg: 'bg-[#F5F5F5]', text: 'text-[#A3A3A3]', label: 'Archived' },
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = { show_archived: showArchived };
      if (filterStatus) params.status = filterStatus;
      
      const response = await adminAPI.getAllOrders(params);
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [filterStatus, showArchived]);

  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.user_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.id?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const handleViewDetail = async (order) => {
    setSelectedOrder(order);
    setShowDetailModal(true);
    try {
      const response = await adminAPI.getOrderDetail(order.id);
      setSelectedOrder(response.data);
    } catch (error) {
      toast.error('Failed to load order details');
    }
  };

  const handleCancelOrder = async (order) => {
    setActionLoading(true);
    try {
      await adminAPI.updateOrderStatus(order.id, 'cancelled');
      toast.success('Order cancelled');
      fetchOrders();
      setShowDetailModal(false);
    } catch (error) {
      toast.error('Failed to cancel order');
    } finally {
      setActionLoading(false);
    }
  };

  const handleArchiveOrder = async (order) => {
    setActionLoading(true);
    try {
      await adminAPI.updateOrderStatus(order.id, 'archived');
      toast.success('Order archived');
      fetchOrders();
      setShowDetailModal(false);
    } catch (error) {
      toast.error('Failed to archive order');
    } finally {
      setActionLoading(false);
    }
  };

  // Stats
  const totalOrders = orders.length;
  const pendingOrders = orders.filter(o => o.status === 'pending').length;
  const completedOrders = orders.filter(o => o.status === 'completed').length;
  const totalRevenue = orders.filter(o => o.status === 'completed').reduce((sum, o) => sum + (o.total || 0), 0);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-[#0A0A0A] tracking-tight">Orders</h1>
            <p className="text-sm text-[#525252] mt-1">{totalOrders} total orders</p>
          </div>
          <button 
            onClick={fetchOrders}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#525252] bg-white border border-[#E5E5E5] rounded-lg hover:bg-[#F5F5F5] transition-all"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white border border-[#EBEBEB] rounded-xl p-5 hover:shadow-md transition-all">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#DBEAFE] flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-[#2563EB]" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-[#0A0A0A] tracking-tight">{totalOrders}</p>
                <p className="text-xs text-[#A3A3A3] uppercase tracking-wider">Total</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#EBEBEB] rounded-xl p-5 hover:shadow-md transition-all">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#FEF3C7] flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-[#D97706]" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-[#D97706] tracking-tight">{pendingOrders}</p>
                <p className="text-xs text-[#A3A3A3] uppercase tracking-wider">Pending</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#EBEBEB] rounded-xl p-5 hover:shadow-md transition-all">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#DCFCE7] flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-[#16A34A]" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-[#16A34A] tracking-tight">{completedOrders}</p>
                <p className="text-xs text-[#A3A3A3] uppercase tracking-wider">Completed</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#EBEBEB] rounded-xl p-5 hover:shadow-md transition-all">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#DCFCE7] flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-[#16A34A]" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-[#16A34A] tracking-tight">{formatPrice(totalRevenue)}</p>
                <p className="text-xs text-[#A3A3A3] uppercase tracking-wider">Revenue</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-[#EBEBEB] rounded-xl p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A3A3A3]" />
              <input
                type="text"
                placeholder="Search orders..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-10 pl-10 pr-4 text-sm border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-black/5 focus:border-[#0A0A0A]"
              />
            </div>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="h-10 px-4 text-sm border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-black/5"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <label className="flex items-center gap-2 text-sm text-[#525252] cursor-pointer">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                className="w-3.5 h-3.5 rounded border-stone-300"
              />
              Show Archived
            </label>
          </div>
        </div>

        {/* Orders Table */}
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-5 h-5 border-2 border-stone-300 border-t-stone-900 rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="bg-white border border-stone-100 rounded overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-stone-50">
                  <th className="text-left p-3 text-[10px] font-semibold text-stone-500 uppercase tracking-wide">Order</th>
                  <th className="text-left p-3 text-[10px] font-semibold text-stone-500 uppercase tracking-wide">Customer</th>
                  <th className="text-left p-3 text-[10px] font-semibold text-stone-500 uppercase tracking-wide">Items</th>
                  <th className="text-center p-3 text-[10px] font-semibold text-stone-500 uppercase tracking-wide">Status</th>
                  <th className="text-right p-3 text-[10px] font-semibold text-stone-500 uppercase tracking-wide">Total</th>
                  <th className="text-right p-3 text-[10px] font-semibold text-stone-500 uppercase tracking-wide">Date</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-50">
                {filteredOrders.map((order) => {
                  const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
                  return (
                    <tr key={order.id} className="hover:bg-stone-50 transition-colors">
                      <td className="p-3">
                        <p className="text-xs font-mono text-stone-600">#{order.id?.slice(0, 8)}</p>
                      </td>
                      <td className="p-3">
                        <p className="text-xs font-medium text-stone-900">{order.user_name}</p>
                        <p className="text-[10px] text-stone-500">{order.user_email}</p>
                      </td>
                      <td className="p-3">
                        <p className="text-xs text-stone-600">{order.items?.length || 0} item(s)</p>
                      </td>
                      <td className="p-3 text-center">
                        <span className={cn(
                          "inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded-full",
                          statusConfig.bg, statusConfig.text
                        )}>
                          {statusConfig.label}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        <p className="text-xs font-semibold text-stone-900">{formatPrice(order.total || 0)}</p>
                      </td>
                      <td className="p-3 text-right">
                        <p className="text-[10px] text-stone-500">{formatDate(order.created_at)}</p>
                      </td>
                      <td className="p-3">
                        <button 
                          onClick={() => handleViewDetail(order)}
                          className="p-1.5 rounded hover:bg-stone-100 transition-colors"
                        >
                          <Eye className="w-3.5 h-3.5 text-stone-400" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filteredOrders.length === 0 && (
              <div className="p-8 text-center">
                <p className="text-xs text-stone-500">No orders found</p>
              </div>
            )}
          </div>
        )}

        {/* Detail Modal */}
        {showDetailModal && selectedOrder && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded w-full max-w-lg max-h-[80vh] overflow-auto m-4">
              <div className="flex items-center justify-between p-4 border-b border-stone-100">
                <h3 className="text-sm font-semibold text-stone-900">Order Details</h3>
                <button onClick={() => setShowDetailModal(false)} className="p-1 hover:bg-stone-100 rounded">
                  <X className="w-4 h-4 text-stone-500" />
                </button>
              </div>
              
              <div className="p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-stone-500">Order ID</span>
                  <span className="text-xs font-mono text-stone-900">{selectedOrder.id}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs text-stone-500">Status</span>
                  <span className={cn(
                    "inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded-full",
                    STATUS_CONFIG[selectedOrder.status]?.bg,
                    STATUS_CONFIG[selectedOrder.status]?.text
                  )}>
                    {STATUS_CONFIG[selectedOrder.status]?.label}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs text-stone-500">Customer</span>
                  <span className="text-xs text-stone-900">{selectedOrder.user_name}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs text-stone-500">Email</span>
                  <span className="text-xs text-stone-600">{selectedOrder.user_email}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs text-stone-500">Total</span>
                  <span className="text-sm font-semibold text-stone-900">{formatPrice(selectedOrder.total)}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs text-stone-500">Date</span>
                  <span className="text-xs text-stone-600">{formatDate(selectedOrder.created_at)}</span>
                </div>

                {/* Items */}
                {selectedOrder.items?.length > 0 && (
                  <div className="pt-3 border-t border-stone-100">
                    <p className="text-xs font-medium text-stone-900 mb-2">Items</p>
                    <div className="space-y-2">
                      {selectedOrder.items.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-stone-50 rounded">
                          <span className="text-xs text-stone-700">{item.service_name}</span>
                          <span className="text-xs font-medium text-stone-900">{formatPrice(item.price)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              {selectedOrder.status !== 'archived' && selectedOrder.status !== 'cancelled' && (
                <div className="flex items-center gap-2 p-4 border-t border-stone-100 bg-stone-50">
                  {selectedOrder.status === 'pending' && (
                    <button
                      onClick={() => handleCancelOrder(selectedOrder)}
                      disabled={actionLoading}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded hover:bg-red-100 transition-all"
                    >
                      <XCircle className="w-3 h-3" />
                      Cancel
                    </button>
                  )}
                  <button
                    onClick={() => handleArchiveOrder(selectedOrder)}
                    disabled={actionLoading}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-stone-600 bg-stone-100 rounded hover:bg-stone-200 transition-all"
                  >
                    <Archive className="w-3 h-3" />
                    Archive
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
